import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { DEFAULT_URL, GET_STARTED_URL, MODEL_WALKTHROUGH_URL, ONBOARDING_URL, SKETCH_URL } from '../app-urls';
import { MODEL_TYPE, TUTORIAL_MODEL_KEY } from '../common/linqset-model';
import { Organisation } from '../common/organisation';
import { ONBOARDING_STEPS } from '../common/user';
import { HelpService } from '../help/help-services';
import { hasDoneStep, ONBOARDING_ACTION, ONBOARDING_CHOICE, onboardingForOrganisationHome, onboardingForOrganisations } from './onboarding';
import { UserService } from './user.service';

// runs the steps of getting started that onboarding.ts decides on. Guidance is only shown on a large screen,
// as models can't be created on a phone
@Injectable({providedIn: 'root'})
export class OnboardingService {

    // leaving getting started is respected until the app is reloaded, so an empty organisation's home page doesn't keep
    // sending the user back to create its first model
    private gettingStartedDismissed = false

    constructor(private userService:UserService, private helpService:HelpService, private router:Router) {}

    // the organisations list, once loaded. Someone with no organisation of their own has nothing to list, so is shown
    // the ways to start instead, each time they come to it. True when they are sent there, so the list isn't shown.
    // The list is replaced in the history, so going back doesn't land on it to be sent on again
    organisationsListed(organisations:Organisation[]):boolean {
        if (onboardingForOrganisations(organisations, environment.demoOrganisationKey) !== ONBOARDING_ACTION.CREATE_ORGANISATION) return false
        this.router.navigateByUrl(GET_STARTED_URL(), {replaceUrl: true})
        return true
    }

    // the user left getting started without creating a model: to their organisation, if they have one. Someone with no
    // organisation of their own goes to the organisations list, which shows them the ways to start again
    dismissGettingStarted(organisationKey?:string) {
        this.gettingStartedDismissed = true
        this.router.navigateByUrl(DEFAULT_URL(organisationKey))
    }

    // the user chose to look round the demo organisation
    exploreDemo() {
        this.gettingStartedDismissed = true
        this.router.navigateByUrl(DEFAULT_URL(environment.demoOrganisationKey))
    }

    // the user chose to learn the basics of modelling. Leaving it brings someone with no organisation back to the ways to start
    learnTheBasics() {
        this.router.navigateByUrl(ONBOARDING_URL())
    }

    // the user chose to walk through the demo organisation
    walkThroughDemo() {
        this.gettingStartedDismissed = true
        this.router.navigateByUrl(MODEL_WALKTHROUGH_URL(environment.demoOrganisationKey))
    }

    // an organisation's home page, once loaded. Resolves to CREATE when the user chose to create a model, which the
    // page does itself; every other choice is handled here
    async organisationHomeLoaded(organisation:Organisation, smallScreen:boolean):Promise<ONBOARDING_CHOICE|undefined> {
        if (smallScreen) return undefined

        switch (onboardingForOrganisationHome(this.userService.getUser(), organisation)) {
            case ONBOARDING_ACTION.CREATE_FIRST_MODEL:
                if (!this.gettingStartedDismissed) this.router.navigateByUrl(GET_STARTED_URL(organisation._key))
                return undefined
            case ONBOARDING_ACTION.HOME_INTRO: {
                const dialogRef = this.helpService.openCreateModelDialog(organisation)
                this.userService.saveOnboardingStep(ONBOARDING_STEPS.HOME_INTRO_SHOWN)
                const choice = await firstValueFrom(dialogRef.afterClosed())
                return this.handleChoice(choice, organisation._key) ? undefined : choice
            }
            default:
                return undefined
        }
    }

    // a model has been opened empty or generated by AI. True the first time, when the user should be shown around it
    firstModelOpened():boolean {
        if (hasDoneStep(this.userService.getUser(), ONBOARDING_STEPS.FIRST_MODEL)) return false
        this.userService.saveOnboardingStep(ONBOARDING_STEPS.FIRST_MODEL)
        return true
    }

    // the choices the getting-started dialogs share. False for one the screen has to handle itself
    handleChoice(choice:ONBOARDING_CHOICE|undefined, organisationKey:string):boolean {
        switch (choice) {
            case ONBOARDING_CHOICE.VIDEOS:
                this.helpService.openIntroductonDialog()
                return true
            case ONBOARDING_CHOICE.WALKTHROUGH:
                this.router.navigateByUrl(ONBOARDING_URL(organisationKey))
                return true
            case ONBOARDING_CHOICE.TUTORIAL:
                this.router.navigateByUrl(SKETCH_URL(organisationKey, TUTORIAL_MODEL_KEY, MODEL_TYPE.PROCESS))
                return true
            case ONBOARDING_CHOICE.HOME:
                this.router.navigateByUrl(DEFAULT_URL(organisationKey))
                return true
            default:
                return false
        }
    }
}
